import React, { useEffect, useState } from "react";
import { api, fmtGNF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Plus, Loader2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const INSTALLMENT_UI = {
  paid: { label: "Payée", color: "bg-green-100 text-green-800" },
  partial: { label: "Partielle", color: "bg-yellow-100 text-yellow-800" },
  pending: { label: "En attente", color: "bg-slate-100 text-slate-700" },
};

export default function PaymentPlansPage() {
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [feeConfigs, setFeeConfigs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: "", academic_year: "2026-2027", plan_type: "monthly",
    total_amount_gnf: 0, registration_fee_gnf: 0, installments_count: 9, start_date: "2026-10-01", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, s, f] = await Promise.allSettled([
      api.get("/scolarite/payment-plans"),
      api.get("/scolarite/students-search"),
      api.get("/scolarite/fee-configs/active"),
    ]);
    if (p.status === "fulfilled") setPlans(p.value.data);
    else toast.error("Impossible de charger les plans de paiement");
    if (s.status === "fulfilled") setStudents(s.value.data);
    if (f.status === "fulfilled") setFeeConfigs(f.value.data);
  };
  useEffect(() => { load(); }, []);

  const useConfig = (cfgId) => {
    const cfg = feeConfigs.find(c => c.id === cfgId);
    if (!cfg) return;
    const reg = cfg.items?.filter(i => i.fee_type === "registration").reduce((s, i) => s + i.amount_gnf, 0) || 0;
    const tui = cfg.items?.filter(i => i.fee_type === "tuition").reduce((s, i) => s + i.amount_gnf, 0) || 0;
    const other = cfg.items?.filter(i => !["registration", "tuition"].includes(i.fee_type) && i.mandatory).reduce((s, i) => s + i.amount_gnf, 0) || 0;
    setForm({ ...form, registration_fee_gnf: reg, total_amount_gnf: tui + other });
    toast.success(`Grille appliquée : ${cfg.class_name}`);
  };

  const submit = async () => {
    if (!form.student_id || !form.total_amount_gnf) return toast.error("Élève + montant requis");
    setSaving(true);
    try {
      await api.post("/scolarite/payment-plans", {
        ...form,
        total_amount_gnf: parseFloat(form.total_amount_gnf),
        registration_fee_gnf: parseFloat(form.registration_fee_gnf) || 0,
        installments_count: parseInt(form.installments_count, 10),
      });
      toast.success("Plan créé");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="payment-plans-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Wallet className="h-3 w-3" /><span>PLANS DE PAIEMENT</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Plans de paiement</h1>
          <p className="text-slate-500 mt-1">Mensuel, annuel, trimestriel ou personnalisé.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-plan-btn">
          <Plus className="h-4 w-4" />Nouveau plan
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Élève</th>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-right">Total net</th>
                <th className="px-4 py-3 text-right">Payé</th>
                <th className="px-4 py-3 text-right">Solde</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`plan-row-${p.id}`}>
                  <td className="px-4 py-2 font-medium">{p.student_name}</td>
                  <td className="px-4 py-2">{p.class_name}</td>
                  <td className="px-4 py-2 text-xs uppercase">{p.plan_type}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtGNF(p.net_amount_gnf)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#166534]">{fmtGNF(p.paid_amount_gnf)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#DC2626] font-bold">{fmtGNF(p.balance_gnf)}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${p.status === "completed" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setDetail(p)} data-testid={`view-plan-${p.id}`}>Détail</Button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Aucun plan</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nouveau plan de paiement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Élève *</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="pp-student"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.id} value={s.id}>{s.matricule} — {s.first_name} {s.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {feeConfigs.length > 0 && (
              <div>
                <Label>Appliquer une grille tarifaire approuvée (optionnel)</Label>
                <Select onValueChange={useConfig}>
                  <SelectTrigger data-testid="pp-usecfg"><SelectValue placeholder="Grille approuvée..." /></SelectTrigger>
                  <SelectContent>
                    {feeConfigs.map(c => <SelectItem key={c.id} value={c.id}>{c.class_name} ({c.academic_year}) — {fmtGNF(c.total_gnf)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type de plan</Label>
                <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                  <SelectTrigger data-testid="pp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="annual">Annuel</SelectItem>
                    <SelectItem value="trimester">Trimestriel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Année scolaire</Label>
                <Input value={form.academic_year} onChange={e => setForm({ ...form, academic_year: e.target.value })} data-testid="pp-year" />
              </div>
              <div>
                <Label>Frais d&apos;inscription (GNF)</Label>
                <Input type="number" value={form.registration_fee_gnf} onChange={e => setForm({ ...form, registration_fee_gnf: e.target.value })} data-testid="pp-reg" />
              </div>
              <div>
                <Label>Total scolarité (GNF)</Label>
                <Input type="number" value={form.total_amount_gnf} onChange={e => setForm({ ...form, total_amount_gnf: e.target.value })} data-testid="pp-total" />
              </div>
              {form.plan_type === "monthly" && (
                <div>
                  <Label>Nombre de mensualités</Label>
                  <Input type="number" value={form.installments_count} onChange={e => setForm({ ...form, installments_count: e.target.value })} data-testid="pp-count" />
                </div>
              )}
              <div>
                <Label>Date début</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="pp-start" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="pp-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer le plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Plan — {detail?.student_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3"><div className="text-xs text-slate-500">Total brut</div><div className="font-bold">{fmtGNF(detail.total_amount_gnf)}</div></div>
                <div className="bg-slate-50 rounded-lg p-3"><div className="text-xs text-slate-500">Remise</div><div className="font-bold text-[#EA580C]">{detail.discount_percentage || 0}%</div></div>
                <div className="bg-slate-50 rounded-lg p-3"><div className="text-xs text-slate-500">Solde</div><div className="font-bold text-[#DC2626]">{fmtGNF(detail.balance_gnf)}</div></div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                  <th className="px-3 py-2 text-left">Échéance</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Montant</th>
                  <th className="px-3 py-2 text-right">Payé</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                </tr></thead>
                <tbody>
                  {detail.installments?.map(inst => {
                    const st = INSTALLMENT_UI[inst.status] || INSTALLMENT_UI.pending;
                    return (
                      <tr key={inst.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-xs">{inst.label}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{inst.due_date}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtGNF(inst.amount_gnf)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#166534]">{fmtGNF(inst.paid_amount || 0)}</td>
                        <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
